import { Engine } from '../Engine';
import { ViewPort } from './ViewPort';
import { Shader } from '../shader/shader';
import { CubeMapTexture, PlaceHolderCubeMapTexture, PlaceHolderTexture, Texture } from '../texture';
import { ResourceLoader } from '../resource/ResourceLoader';

export class Context {
  private currentShader: Shader | null;
  public textureState: TextureState;
  public shaderSwitchCount: number = 0;

  constructor(
    public engine: Engine,
    public gl: WebGL2RenderingContext,
    public viewPort: ViewPort,
    public resourceLoader: ResourceLoader,
  ) {
    this.currentShader = null;

    this.textureState = new TextureState(this);
  }

  public resetState(): void {
    if (this.currentShader) {
      this.currentShader.disable();
      this.currentShader = null;
    }
    this.textureState.clear();
  }

  public setShader(shader: Shader) {
    if (this.currentShader !== shader) {
      if (this.currentShader) {
        this.currentShader.disable();
      }

      this.currentShader = shader;
      this.gl.useProgram(this.currentShader.program);

      this.currentShader.enable();
      this.shaderSwitchCount++;
    }
    this.currentShader.next();
    return this.currentShader;
  }
}

export class TextureState {
  // Textures are set on global level so when switching between shaders we have to make sure cubes/2d textures
  // are not mixed due to the default uniform location values when a texture is not actually set/intended to be used.

  private textureMap: Map<number, Texture>;

  public placeHolder2d: Texture;
  public placeHolderCubeMap: CubeMapTexture;

  constructor(private ctx: Context) {
    this.textureMap = new Map();
    this.placeHolder2d = new PlaceHolderTexture(this.ctx);
    this.placeHolderCubeMap = new PlaceHolderCubeMapTexture(this.ctx);
  }

  public setTexture(position: number, texture: Texture) {
    const gl = this.ctx.gl;

    // NOTE: we deliberately do NOT short-circuit when textureMap.get(position)
    // === texture. Several engine paths call gl.bindTexture() raw (e.g.
    // createColorTexture in RenderTarget.ts, LightDataTexture.update,
    // DataTexture.createTexture, Texture.uploadImageData) without going
    // through TextureState. Those raw binds hijack whatever slot is currently
    // active (usually slot 0) and leave the JS cache out of sync with the
    // real GL binding. If we trust the JS cache and skip the rebind, the
    // shader can end up sampling whatever raw texture last hijacked the slot
    // — and if that happens to be an FBO color attachment (e.g. the dummy
    // color texture of a RenderedTexture shadow map), WebGL fires the
    // feedback-loop INVALID_OPERATION on the very next draw.
    this.textureMap.set(position, texture);

    const texturePosition = gl.TEXTURE0 + position;
    gl.activeTexture(texturePosition);
    if (texture.is2dArray) {
      gl.bindTexture(gl.TEXTURE_2D, null);
      gl.bindTexture(gl.TEXTURE_CUBE_MAP, null);
      gl.bindTexture(gl.TEXTURE_2D_ARRAY, texture.glTexture);
    } else if (texture.is2d) {
      gl.bindTexture(gl.TEXTURE_CUBE_MAP, null);
      gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
      gl.bindTexture(gl.TEXTURE_2D, texture.glTexture);
    } else {
      gl.bindTexture(gl.TEXTURE_2D, null);
      gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
      gl.bindTexture(gl.TEXTURE_CUBE_MAP, texture.glTexture);
    }
  }

  public getTexture(position: number): Texture | null {
    return this.textureMap.get(position) ?? null;
  }

  public getTexturePosition(texture: Texture): number | undefined {
    let result: number | undefined = undefined;
    this.textureMap.forEach((v, k) => {
      if (v === texture) {
        result = k;
      }
    });

    return result;
  }

  public clear(): void {
    // Physically unbind from every GL texture unit we know about so the JS
    // cache stays in sync with real GL state. Without this, a subsequent draw
    // can sample a stale binding (e.g. an FBO attachment) via a sampler
    // uniform that still points at the slot, producing a feedback loop.
    const gl = this.ctx.gl;
    for (const position of this.textureMap.keys()) {
      gl.activeTexture(gl.TEXTURE0 + position);
      gl.bindTexture(gl.TEXTURE_2D, null);
      gl.bindTexture(gl.TEXTURE_CUBE_MAP, null);
      gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
    }
    this.textureMap.clear();
  }
}
